import React from 'react'
import { Link } from 'react-router-dom'
import { Pencil, User } from 'lucide-react'
import { cn } from '../../lib/utils'

const getInitials = profile => {
  const first = profile?.first_name?.[0] || ''
  const last = profile?.last_name?.[0] || ''
  return `${first}${last}`.toUpperCase() || 'U'
}

const AccountProfileHeader = ({
  profile,
  variant = 'mobile',
  className,
  showEdit = true
}) => {
  const fullName = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .join(' ')
  const pronouns = profile?.pronouns
  const image = profile?.image
  const isSidebar = variant === 'sidebar'
  const washHeight = isSidebar ? 64 : 80
  // Pull avatar up so it sits on the wash curve; later DOM order keeps it above
  const avatarOverlap = isSidebar ? 40 : 48

  return (
    <div
      className={cn(
        'relative flex flex-col items-center text-foreground',
        isSidebar ? 'gap-3 pb-4' : 'gap-4 px-4 pb-0',
        className
      )}
    >
      <div
        className="pointer-events-none relative w-full overflow-hidden text-primary/15"
        style={{ height: washHeight }}
        aria-hidden
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 390 80"
          fill="none"
          preserveAspectRatio="none"
          className="absolute inset-0 block size-full"
        >
          <path d="M0 0h390v80Q195 36 0 80Z" fill="currentColor" />
        </svg>
      </div>

      <div className="relative" style={{ marginTop: -avatarOverlap }}>
        {/* Opaque bg-background base so wash cannot show through primary/15 tint */}
        <div className="size-16 overflow-hidden rounded-full border-8 border-background bg-background">
          {image ? (
            <img
              src={image}
              alt={fullName || 'Profile'}
              className="size-full object-cover"
            />
          ) : (
            <div className="flex size-full items-center justify-center bg-primary/20 text-foreground">
              {fullName ? (
                <span className="text-lg font-semibold text-foreground">
                  {getInitials(profile)}
                </span>
              ) : (
                <User className="size-7 text-foreground" strokeWidth={1.5} />
              )}
            </div>
          )}
        </div>
        {showEdit ? (
          <Link
            to="/my-account/edit-profile"
            className="absolute -bottom-0.5 -right-0.5 flex size-7 items-center justify-center rounded-full border border-border bg-background text-foreground shadow-sm"
            aria-label="Edit profile"
          >
            <Pencil
              className="size-3.5 text-secondary-foreground"
              strokeWidth={1.75}
            />
          </Link>
        ) : null}
      </div>

      <div
        className={cn(
          'relative flex w-full flex-col items-center gap-1 text-center',
          isSidebar ? 'px-3' : null
        )}
      >
        <div className="flex max-w-full flex-wrap items-baseline justify-center gap-1 px-2">
          <p className="truncate text-xl font-medium text-foreground">
            {fullName || 'My account'}
          </p>
          {pronouns ? (
            <p className="max-w-[120px] truncate text-sm font-medium text-secondary-foreground">
              ({pronouns})
            </p>
          ) : null}
        </div>
        {profile?.email ? (
          <p className="truncate text-sm text-secondary-foreground">
            {profile.email}
          </p>
        ) : null}
        {profile?.phone ? (
          <p className="truncate text-sm text-secondary-foreground">
            {profile.phone_prefix
              ? `${profile.phone_prefix} - ${profile.phone}`
              : profile.phone}
          </p>
        ) : null}
      </div>
    </div>
  )
}

export default AccountProfileHeader
